// Design knobs for the super-compact mobile HUD strip: everything —
// globe, capability line, takeover-risk band, date — in one 76px-tall
// bottom strip. The risk band's look and its relationship to the line
// are NOT variant knobs — they live in mobileStripBandSettings, edited
// on /zoom-editor.

// Total strip height, including the graph — the whole compact-HUD budget.
export const MOBILE_STRIP_HEIGHT_PX = 76

export type MobileStripDesignId =
  | "strip-micro-dither"

export type MobileStripVariantConfig = {
  // "card" = flat borderless opaque page-background card;
  // "flush" = full-bleed strip with only a black top rule.
  chrome: "card" | "flush"
  marginPx: number
  radiusPx: number
  // "squish" = self-contained chart: revealed history squished into the
  // panel, log y fit to line + band. "desktop" = mirror of the desktop
  // HUD's pre-choice close-up (HUD87 line styling, linear 1.1–3 axis,
  // generated hug-the-line risk band; future segment solid, not dashed).
  graphSource: "squish" | "desktop"
  // Headroom multiplier above the band top (band-top mode).
  yHeadroomFactor: number
  // "band-top" zooms the y-axis to the solid band's top; "loc-max" keeps
  // the honest frame (squish: real lossOfControl max; desktop: 1.1–3).
  yMaxMode: "band-top" | "loc-max"
  // "text" = bare year; "pill-filled" = black rounded-full pill;
  // "pill-outline" = outlined pill (what's behind shows inside).
  dateStyle: "text" | "pill-filled" | "pill-outline"
  // null offset = vertically centered; oversized globes overflow the
  // strip (clipped by the card unless chrome is "flush").
  globeSizePx: number
  globeTopOffsetPx: number | null
  // Canvas left edge relative to the card. The earth sphere is only
  // ~55% of the canvas, so a bigger canvas pushes the sphere right/down.
  globeLeftPx: number
  // Multiplies sphere-rim, coastline, and datacenter edge strokes.
  globeLineWidthScale: number
  // Widens datacenter footprints on THIS globe only — production
  // footprints are sub-2px specks at strip size. > 1 outgrows the cell.
  datacenterWidthScale: number
  // Strip-only tower-height cap (ignored while datacenterFlat is true):
  // the tallest possible tower as a fraction of the sphere radius. An
  // ABSOLUTE ceiling, not a multiplier — normalizedCompute reaches 1.0
  // at the story's compute peak and must stay inside the globe canvas.
  datacenterMaxHeightFrac: number
  // Height response curve (the site default 2 crushes mid-story compute
  // to near-flat; lower keeps mid-story towers visible).
  datacenterHeightPower: number
  // true = datacenters as pure flat red footprints (no extrusion).
  datacenterFlat: boolean
  // Douglas-Peucker tolerance (degrees) for coastline rings; null = full
  // 110m detail. ~1.5 reads clean at 92px.
  coastlineSimplifyDeg: number | null
  // Absolute sphere-rim stroke; null = site default x globeLineWidthScale.
  globeSphereOutlineWidthPx: number | null
  // true = MobileHUD drops the card's clipping and mounts the globe
  // OUTSIDE the card, so the earth overlaps the page above the strip
  // while the dither/graph/pill still clip to the rounded card.
  globeOverflowsCard: boolean
  // Bottom-sheet grabber bar at the strip's top edge, knocked out of the
  // dither — the "pull this open" affordance. null = no handle.
  // draggable = MobileHUD mounts a hotspot so the bar really drags.
  expandHandle: { widthPx: number; heightPx: number; draggable: boolean } | null
  // true = no earth at all; the date pill carries the visual anchor.
  globeHidden: boolean
  datePillPlacement: "bottom-right" | "top-left"
  datePillFontPx: number
  datePillPaddingPx: { x: number; y: number }
  // true = the ENTIRE collapsed strip drags the sheet up (a still tap
  // opens). The strip then owns every touch, so reading swipes that
  // start on it lift the sheet — see stripHoldToLiftMs for the fix.
  stripDragAnywhere: boolean
  // Hold-to-lift (use with stripDragAnywhere false): swipes that start
  // moving right away scroll the page; resting the finger this many ms
  // first ARMS the lift. A quick still tap opens as usual. null = off.
  stripHoldToLiftMs: number | null
  // Full-open overlay: drag-only swipe-down dismiss zone extending this
  // far down from the card's top edge. null = pill hotspot only.
  sheetDismissZoneHeightPx: number | null
  // Middle resting size: one pull parks the card top at
  // (1 - viewportFraction) of the screen, page behind stays scrollable;
  // a strip tap opens to HALF. null = the sheet knows closed and full.
  sheetHalfDetent: { viewportFraction: number } | null
  // Collapsed strip height (the sheet's smallest position).
  stripHeightPx: number
}

export const COMPACT_STRIP_VARIANTS: Record<
  MobileStripDesignId,
  MobileStripVariantConfig
> = {
  // The shipped design: micro halftone (0.7px dots on a staggered 2.2px
  // grid, no boundary rule), flat paper-colored card, outlined year pill,
  // top grabber bar, hold-to-lift strip surface, thinned globe linework
  // with flat datacenter footprints and simplified coastline.
  "strip-micro-dither": {
    chrome: "card",
    marginPx: 10,
    radiusPx: 24,
    graphSource: "desktop",
    yHeadroomFactor: 1.15,
    yMaxMode: "loc-max",
    dateStyle: "pill-outline",
    globeSizePx: 116,
    // Lifts the sphere so ~10px pokes above the card's top edge — the
    // earth reads as sitting ON the bar rather than boxed inside it.
    // Tuned empirically: the sphere rides high in its canvas.
    globeTopOffsetPx: -20,
    globeLeftPx: 4,
    globeLineWidthScale: 0.5,
    datacenterWidthScale: 2,
    // Peak tower = 0.4 x sphere radius ≈ 13px at the 64px earth — sized
    // so even the story's compute peak stays inside the canvas margins.
    datacenterMaxHeightFrac: 0.4,
    // Gentler than the site's power-2 curve so mid-story sites hold a
    // visible share of the capped peak instead of hugging the surface.
    datacenterHeightPower: 0.6,
    datacenterFlat: false,
    coastlineSimplifyDeg: 1.5,
    globeSphereOutlineWidthPx: null,
    globeOverflowsCard: true,
    expandHandle: { widthPx: 52, heightPx: 5, draggable: true },
    globeHidden: false,
    datePillPlacement: "bottom-right",
    datePillFontPx: 12,
    datePillPaddingPx: { x: 9, y: 5 },
    stripDragAnywhere: false,
    stripHoldToLiftMs: 150,
    sheetDismissZoneHeightPx: null,
    sheetHalfDetent: null,
    stripHeightPx: MOBILE_STRIP_HEIGHT_PX,
  },
}
